/**
 * The words of the missing-permissions screen, as functions of what the kernel
 * said (LO-209).
 *
 * The rule an app opens by is the kernel's: a person may open an app only if
 * they hold every tag its approved manifest reads or writes with. When they do
 * not, the kernel refuses with `403 insufficient_access` and a `missing` list of
 * `{ type, tag, access }`, and `GET /apps/{app}/my-access` gives the same list
 * with the person's own requests beside it. Everything the screen says about
 * *what* is missing is built here from that list and nothing else, so a type
 * added to the kernel needs no change in the UI and a tag is never named from a
 * copy of the vocabulary.
 *
 * No React and no imports: the component, the gate and the tests share it, and
 * keeping it a plain module is what lets them be checked without a DOM.
 */

/** The kernel's code for a person who lacks what an app needs. */
export const ACCESS_REQUIRED = "insufficient_access";

/** True for the kernel's refusal of a person's access, and only that. A
 *  `not_in_manifest` is a different sentence about a different decision. */
export const isAccessRefusal = (status, body) =>
  status === 403 && body?.error === ACCESS_REQUIRED;

/**
 * `missing`, as a clean list: an entry with no tag says nothing a person can
 * ask for and is dropped; `write` stays `write` and anything else is a read; an
 * entry listed twice is listed once.
 */
export function normaliseMissing(missing) {
  const out = [];
  const seen = new Set();
  for (const m of Array.isArray(missing) ? missing : []) {
    if (!m?.tag) continue;
    const entry = {
      type: String(m.type ?? ""),
      tag: String(m.tag),
      access: m.access === "write" ? "write" : "read",
    };
    const key = `${entry.type}\u0000${entry.tag}\u0000${entry.access}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(entry);
  }
  return out;
}

/** Missing items grouped by tag, in the order the kernel listed them: a request
 *  is for a tag, so the screen is drawn per tag. */
export function byTag(missing) {
  const groups = [];
  for (const item of normaliseMissing(missing)) {
    let group = groups.find((g) => g.tag === item.tag);
    if (!group) groups.push((group = { tag: item.tag, items: [] }));
    group.items.push(item);
  }
  return groups;
}

/** `a`, `a and b`, `a, b and c`. */
export function list(words) {
  const w = [...words];
  return w.length < 2 ? w.join("") : `${w.slice(0, -1).join(", ")} and ${w.at(-1)}`;
}

const unique = (words) => [...new Set(words)];

/** `Payment and Invoice records tagged finance`, one per tag, for one kind of access. */
function clauses(missing, access) {
  return byTag(normaliseMissing(missing).filter((m) => m.access === access)).map(
    (g) => `${list(unique(g.items.map((i) => i.type)))} records tagged ${g.tag}`,
  );
}

/**
 * What the app uses that the person does not hold, in two sentences: what the
 * app does with it, and which tags the person lacks. From the list alone.
 *
 * With nothing listed it says that, rather than inventing a reason: a refusal
 * the kernel did not itemise is still information, and "the kernel did not say
 * which" is the whole of it.
 */
export function explain(missing) {
  const items = normaliseMissing(missing);
  if (!items.length) {
    return {
      what: "The kernel refused this without saying what is missing.",
      lack: "",
    };
  }
  const shown = clauses(items, "read");
  const changed = clauses(items, "write");
  const parts = [];
  if (shown.length) parts.push(`shows ${list(shown)}`);
  if (changed.length) parts.push(`changes ${list(changed)}`);
  return {
    what: `This app ${parts.join(" and ")}.`,
    lack: `You don't hold ${list(unique(items.map((m) => m.tag)))}.`,
  };
}

/** The four things a request can be, and one for a state this does not know. */
export function kindOf(request) {
  if (!request) return "none";
  const state = String(request.state ?? "").toLowerCase();
  if (state === "pending") return "pending";
  if (state === "approved") return "approved";
  if (state === "declined" || state === "denied" || state === "rejected") return "declined";
  return "other";
}

const RANK = { pending: 3, approved: 2, declined: 1, other: 0 };

/**
 * The request that matters for a tag: a pending one first (it is what the
 * person is waiting on), then an approved one, then a declined one; among equals
 * the later in the list. `null` when the person has asked for nothing.
 */
export function requestFor(requests, tag) {
  let best = null;
  for (const r of Array.isArray(requests) ? requests : []) {
    if (r?.tag !== tag) continue;
    if (!best || RANK[kindOf(r)] >= RANK[kindOf(best)]) best = r;
  }
  return best;
}

/** One name for whoever the kernel routed a request to, whatever shape it gave. */
function nameOf(who) {
  if (typeof who === "string") return who.trim();
  if (who && typeof who === "object") {
    for (const key of ["name", "display_name", "email", "username", "user", "id"]) {
      if (typeof who[key] === "string" && who[key].trim()) return who[key].trim();
    }
  }
  return "";
}

/** `routed_to`, as words: the names it can read, and nothing it cannot. */
export function whoText(routed) {
  return list((Array.isArray(routed) ? routed : []).map(nameOf).filter(Boolean));
}
